import React, { useMemo } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '../../../stores/ThemeContext';

const WEAKNESS_COLORS = {
  emotion: '#ef4444',
  risk: '#f59e0b',
  strategy: '#3b82f6',
  tied: '#71717a',
};

function getDominantWeakness(emotion, risk, strategy) {
  const min = Math.min(emotion, risk, strategy);
  const weaknesses = [];
  if (emotion === min) weaknesses.push('emotion');
  if (risk === min) weaknesses.push('risk');
  if (strategy === min) weaknesses.push('strategy');
  return weaknesses.length === 1 ? weaknesses[0] : 'tied';
}

function linearRegression(points) {
  const n = points.length;
  if (n < 2) return null;

  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  for (const { composite, profit } of points) {
    sumX += composite;
    sumY += profit;
    sumXY += composite * profit;
    sumX2 += composite * composite;
  }

  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return null;

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;

  const minX = Math.min(...points.map(p => p.composite));
  const maxX = Math.max(...points.map(p => p.composite));

  return [
    { x: minX, y: slope * minX + intercept },
    { x: maxX, y: slope * maxX + intercept },
  ];
}

const CustomTooltip = ({ active, payload, isDark }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const tooltipBg     = isDark ? '#1c1c1e' : '#ffffff';
  const tooltipBorder = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.12)';
  const tooltipText   = isDark ? '#fafafa' : '#111111';
  return (
    <div
      style={{
        backgroundColor: tooltipBg,
        border: tooltipBorder,
        borderRadius: 8,
        padding: '8px 12px',
        fontSize: 12,
        color: tooltipText,
      }}
    >
      <p className="font-semibold mb-1">{new Date(d.date).toLocaleDateString()}</p>
      <p>Emotion: <span style={{ color: '#8b5cf6' }}>{d.emotion}</span></p>
      <p>Risk: <span style={{ color: '#f59e0b' }}>{d.risk}</span></p>
      <p>Strategy: <span style={{ color: '#14b8a6' }}>{d.strategy}</span></p>
      <p>Composite: <span className="font-semibold">{d.composite}</span></p>
      <p>P&L: <span className={`font-mono tabular-nums ${d.profit >= 0 ? 'text-[#4ADE80]' : 'text-[#F87171]'}`}>
        ${d.profit.toFixed(2)}
      </span></p>
    </div>
  );
};

const ScoreCorrelation = ({ data }) => {
  const { isDark } = useTheme();

  const gridColor  = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
  const axisColor  = isDark ? '#a1a1aa' : '#52525b';
  const dimColor   = isDark ? '#71717a' : '#71717a';

  const { scatterData, trendLine } = useMemo(() => {
    const entries = data?.score_entries || [];
    if (entries.length === 0) return { scatterData: [], trendLine: null };

    const processed = entries.map(e => {
      const composite = Math.round(((e.emotion + e.risk + e.strategy) / 3) * 10) / 10;
      const weakness = getDominantWeakness(e.emotion, e.risk, e.strategy);
      return {
        ...e,
        composite,
        weakness,
        fill: WEAKNESS_COLORS[weakness],
      };
    });

    return {
      scatterData: processed,
      trendLine: linearRegression(processed),
    };
  }, [data]);

  if (scatterData.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <h3 className="text-sm font-semibold text-foreground mb-1">Score vs. P&L Correlation</h3>
        <p className="text-xs text-muted-foreground mb-4">Composite score mapped against profit</p>
        <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
          No scored journals yet. Complete journals with scores to see correlations.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <h3 className="text-sm font-semibold text-foreground mb-1">Score vs. P&L Correlation</h3>
      <p className="text-xs text-muted-foreground mb-2">Composite score mapped against profit</p>
      <div className="flex flex-wrap gap-3 mb-3">
        {Object.entries(WEAKNESS_COLORS).map(([key, color]) => (
          <div key={key} className="flex items-center gap-1 text-xs text-muted-foreground">
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: color }} />
            {key === 'tied' ? 'Equal' : key.charAt(0).toUpperCase() + key.slice(1)} weakness
          </div>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            type="number"
            dataKey="composite"
            name="Score"
            domain={[0, 100]}
            tick={{ fill: axisColor, fontSize: 11 }}
            label={{ value: 'Composite Score', position: 'insideBottom', offset: -5, fill: dimColor, fontSize: 11 }}
          />
          <YAxis
            type="number"
            dataKey="profit"
            name="P&L"
            tick={{ fill: axisColor, fontSize: 11 }}
            label={{ value: 'P&L ($)', angle: -90, position: 'insideLeft', fill: dimColor, fontSize: 11 }}
          />
          <ReferenceLine y={0} stroke={dimColor} strokeDasharray="4 3" />
          {trendLine && (
            <ReferenceLine
              segment={trendLine.map(p => ({ x: p.x, y: p.y }))}
              stroke="#6366f1"
              strokeWidth={2}
              strokeDasharray="6 3"
              ifOverflow="extendDomain"
            />
          )}
          <Tooltip content={<CustomTooltip isDark={isDark} />} />
          <Scatter
            data={scatterData}
            shape={(props) => {
              const { cx, cy, payload } = props;
              return (
                <circle
                  cx={cx}
                  cy={cy}
                  r={5}
                  fill={payload.fill}
                  fillOpacity={0.8}
                  stroke={payload.fill}
                  strokeWidth={1}
                />
              );
            }}
          />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ScoreCorrelation;
